import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Clock, Bell, Plus } from 'lucide-react';
import { apiGet } from '../lib/api';
import { getEntityUrl, getRecentSearches, saveRecentSearch } from '../lib/entityUrls';
import { ENTITY_ICONS, ENTITY_COLORS, ENTITY_LABELS } from '../lib/entityMeta';
import type { EntitySearchResult } from '../lib/entityUrls';

type SearchResult = EntitySearchResult;

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}


export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [recentSearches] = useState(getRecentSearches);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      setResults([]);
      setSelectedIndex(0);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await apiGet<SearchResult[]>(`/entity-search?q=${encodeURIComponent(query.trim())}&limit=20`);
        setResults(data);
        setSelectedIndex(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, open]);

  // Scroll selected into view
  useEffect(() => {
    const container = resultsRef.current;
    if (!container) return;
    const active = container.querySelector('[data-active="true"]') as HTMLElement | null;
    active?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const handleSelect = useCallback((result: SearchResult) => {
    saveRecentSearch(query);
    navigate(getEntityUrl(result));
    onClose();
  }, [query, navigate, onClose]);

  const handleRecentClick = useCallback((q: string) => {
    setQuery(q);
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    const total = quickActions.length + results.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % Math.max(total, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + total - 1) % Math.max(total, 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex < quickActions.length) {
        quickActions[selectedIndex].action();
      } else if (results[selectedIndex - quickActions.length]) {
        handleSelect(results[selectedIndex - quickActions.length]);
      } else if (query.trim()) {
        // Navigate to full search page
        saveRecentSearch(query);
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
        onClose();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  if (!open) return null;

  // Quick actions that match the query
  const quickActions: { id: string; label: string; icon: typeof Bell; color: string; action: () => void }[] = [];
  const ql = query.toLowerCase();
  if (ql && ('reminder'.includes(ql) || 'remind'.startsWith(ql) || ql.includes('remind'))) {
    quickActions.push(
      { id: 'go-reminders', label: 'Go to Reminders', icon: Bell, color: '#f59e0b', action: () => { navigate('/reminders'); onClose(); } },
      { id: 'new-reminder', label: 'Create Reminder', icon: Plus, color: '#22c55e', action: () => { navigate('/reminders'); onClose(); } },
    );
  }

  // Group results by entity type
  const grouped = new Map<string, SearchResult[]>();
  for (const r of results) {
    const group = grouped.get(r.entity_type) ?? [];
    group.push(r);
    grouped.set(r.entity_type, group);
  }

  // Flat list for index tracking (quick actions first)
  let flatIndex = 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/60" onClick={onClose}>
      <div
        className="mx-auto mt-[20vh] w-full max-w-lg bg-bg-surface border border-border rounded-xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search size={18} className="text-text-muted flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search everything..."
            className="flex-1 bg-transparent text-text-primary placeholder:text-text-muted focus:outline-none text-sm"
          />
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border border-accent border-t-transparent flex-shrink-0" />
          )}
        </div>

        {/* Results */}
        <div ref={resultsRef} className="max-h-[50vh] overflow-y-auto">
          {query.trim() === '' ? (
            // Show recent searches
            recentSearches.length > 0 ? (
              <div className="py-2">
                <div className="px-4 py-1.5 text-[10px] font-semibold text-text-muted uppercase tracking-wider">
                  Recent Searches
                </div>
                {recentSearches.map(q => (
                  <button
                    key={q}
                    onClick={() => handleRecentClick(q)}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-text-secondary hover:bg-bg-hover transition-colors text-left"
                  >
                    <Clock size={14} className="text-text-muted flex-shrink-0" />
                    {q}
                  </button>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-sm text-text-muted">
                Start typing to search...
              </div>
            )
          ) : results.length === 0 && !loading ? (
            <div className="py-8 text-center text-sm text-text-muted">
              No results for "{query}"
            </div>
          ) : (
            <div className="py-2">
              {quickActions.length > 0 && (
                <div>
                  <div className="px-4 py-1.5 text-[10px] font-semibold text-text-muted uppercase tracking-wider">
                    Quick Actions
                  </div>
                  {quickActions.map(qa => {
                    const idx = flatIndex++;
                    return (
                      <button
                        key={qa.id}
                        data-active={idx === selectedIndex}
                        onClick={qa.action}
                        onMouseEnter={() => setSelectedIndex(idx)}
                        className={`flex items-center gap-3 w-full px-4 py-2 text-sm transition-colors text-left ${
                          idx === selectedIndex ? 'bg-bg-hover text-text-primary' : 'text-text-secondary hover:bg-bg-hover'
                        }`}
                      >
                        <qa.icon size={16} className="flex-shrink-0" style={{ color: qa.color }} />
                        <span className="flex-1 truncate">{qa.label}</span>
                        <span className="text-[10px] text-text-muted uppercase">Action</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {Array.from(grouped.entries()).map(([type, items]) => (
                <div key={type}>
                  <div className="px-4 py-1.5 text-[10px] font-semibold text-text-muted uppercase tracking-wider">
                    {ENTITY_LABELS[type] ?? type}
                  </div>
                  {items.map(item => {
                    const idx = flatIndex++;
                    const Icon = ENTITY_ICONS[item.entity_type] ?? FileText;
                    const iconColor = ENTITY_COLORS[item.entity_type] ?? '#6366f1';
                    return (
                      <button
                        key={`${item.entity_type}-${item.entity_id}`}
                        data-active={idx === selectedIndex}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setSelectedIndex(idx)}
                        className={`flex items-center gap-3 w-full px-4 py-2 text-sm transition-colors text-left ${
                          idx === selectedIndex ? 'bg-bg-hover text-text-primary' : 'text-text-secondary hover:bg-bg-hover'
                        }`}
                      >
                        <Icon size={16} className="flex-shrink-0" style={{ color: iconColor }} />
                        <span className="flex-1 truncate">{item.name}</span>
                        <span className="text-[10px] text-text-muted uppercase">{ENTITY_LABELS[item.entity_type]}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
              {/* Footer hint */}
              <div className="px-4 py-2 border-t border-border mt-1">
                <span className="text-[10px] text-text-muted">
                  Press Enter to search all, ↑↓ to navigate, Esc to close
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
